import { useMemo } from "react";

const DAY = 86400000;
const SHUT = ["2026-12-24", "2027-01-10"];
const toD = s => new Date(`${s}T00:00:00Z`);
const iso = d => d.toISOString().slice(0, 10);
const LABEL = "18rem";
const at = p => `calc(${LABEL} + (100% - ${LABEL}) * ${p / 100})`;

function useLayout(items, todayIso) {
  return useMemo(() => {
    const dated = items.filter(i => i.start_date);
    if (!dated.length) return null;
    const ends = dated.map(i => i.end_date || i.start_date);
    const min = toD(dated.map(i => i.start_date).sort()[0]);
    const max = toD(ends.sort().slice(-1)[0]);
    const total = Math.round((max - min) / DAY) + 1;
    const pct = s => Math.max(0, Math.min(100, ((toD(s) - min) / DAY / total) * 100));
    const groups = [];
    for (const i of [...dated].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.start_date.localeCompare(b.start_date))) {
      const ph = i.phase || "Other";
      let g = groups.find(x => x.phase === ph);
      if (!g) { g = { phase: ph, items: [] }; groups.push(g); }
      g.items.push(i);
    }
    const months = [];
    const m = new Date(Date.UTC(min.getUTCFullYear(), min.getUTCMonth() + 1, 1));
    for (; m <= max; m.setUTCMonth(m.getUTCMonth() + 1)) months.push({ p: pct(iso(m)), label: m.toLocaleDateString("en-NZ", { month: "short", year: "2-digit", timeZone: "UTC" }) });
    const shutOn = SHUT[0] <= iso(max) && SHUT[1] >= iso(min);
    const shut = shutOn ? { l: pct(SHUT[0] < iso(min) ? iso(min) : SHUT[0]),
      r: pct(iso(new Date(toD(SHUT[1] > iso(max) ? iso(max) : SHUT[1]).getTime() + DAY))) } : null;
    const todayOn = todayIso >= iso(min) && todayIso <= iso(max);
    return { min, max, total, pct, groups, months, shut, today: todayOn ? pct(todayIso) + (100 / total) / 2 : null };
  }, [items, todayIso]);
}

const Bar = ({ i, pct, total }) => {
  if (i.milestone) return (
    <div className="absolute top-1/2 w-3 h-3 -mt-1.5 -ml-1.5 rotate-45 bg-amber-400 border border-amber-200"
      style={{ left: `${pct(i.start_date) + 50 / total}%` }} data-testid={`gantt-milestone-${i.id}`}/>
  );
  const end = iso(new Date(toD(i.end_date || i.start_date).getTime() + DAY));
  const l = pct(i.start_date);
  return (
    <div className={`absolute top-0.5 bottom-0.5 rounded border ${i.done ? "bg-green-600/70 border-green-400" : "bg-silver/80 border-silver"}`}
      style={{ left: `${l}%`, width: `${Math.max(0.4, ((toD(end) - toD(i.start_date)) / DAY / total) * 100)}%` }} data-testid={`gantt-bar-${i.id}`}/>
  );
};

export default function Gantt({ items, todayIso, onPick }) {
  const L = useLayout(items, todayIso);
  if (!L) return null;
  return (
    <div className="card overflow-x-auto" data-testid="programme-gantt">
      <div className="relative min-w-[1100px]">
        <div className="flex text-[10px] opacity-70 h-5 relative" style={{ marginLeft: LABEL }}>
          {L.months.map(m => <div key={m.label} className="absolute border-l border-neutral-700 pl-1" style={{ left: `${m.p}%` }}>{m.label}</div>)}
        </div>
        {L.shut && <div className="absolute top-5 bottom-0 bg-red-900/30 border-x border-red-700/60 pointer-events-none z-0"
          style={{ left: at(L.shut.l), width: `calc((100% - ${LABEL}) * ${(L.shut.r - L.shut.l) / 100})` }}
          title="Christmas shutdown 24 Dec 2026 to 10 Jan 2027" data-testid="gantt-shutdown"/>}
        {L.today !== null && <div className="absolute top-5 bottom-0 w-px bg-red-500 z-20 pointer-events-none" style={{ left: at(L.today) }} data-testid="gantt-today-line"/>}
        {L.groups.map(g => (
          <div key={g.phase} data-testid={`gantt-phase-${g.phase}`}>
            <div className="text-xs text-silver font-semibold pt-2 pb-1 border-b border-neutral-800">{g.phase}</div>
            {g.items.map(i => (
              <button type="button" key={i.id} onClick={() => onPick(i)} className="flex w-full items-center text-xs hover:bg-neutral-900 text-left" data-testid={`gantt-row-${i.id}`}>
                <div className="shrink-0 truncate pr-2 py-0.5" style={{ width: LABEL }} title={i.name}>
                  <span className="opacity-50 mr-1">{i.ext_id}</span>{i.done ? "✓ " : ""}{i.name}
                </div>
                <div className="flex-1 h-5 relative z-10"><Bar i={i} pct={L.pct} total={L.total}/></div>
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-xs opacity-70 mt-2">
        <span><span className="inline-block w-3 h-3 bg-silver/80 align-middle mr-1"/>Task</span>
        <span><span className="inline-block w-3 h-3 bg-green-600/70 align-middle mr-1"/>Done</span>
        <span><span className="inline-block w-2.5 h-2.5 rotate-45 bg-amber-400 align-middle mr-1"/>Milestone</span>
        <span><span className="inline-block w-3 h-3 bg-red-900/50 align-middle mr-1"/>Christmas shutdown</span>
        <span><span className="inline-block w-px h-3 bg-red-500 align-middle mr-1"/>Today</span>
      </div>
    </div>
  );
}
